import { TerraLink } from "../../component/TerraLink/TerraLink"
import { CardGeneral } from "../../component/CardGeneral/CardGeneral"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import { SpecialButton } from "../../component/SpecialButton/SpecialButton"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"

export interface LayoutCards {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  cardType?: "bound" | "unbound" | "categorized"
  buttons?: TerraLink[]
  cards?: CardGeneral[]
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  isWrapped?: boolean
  isButtonTopAligned?: boolean
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

const HEADER_AREA =
  "basis-full max-w-full px-2.5 ml-0 min-lg:basis-[66.666%] min-lg:max-w-[66.666%]"

const TOP_CTA =
  "basis-full max-w-full px-2.5 ml-0 min-lg:basis-[33.333%] min-lg:max-w-[33.333%] flex justify-end"

const CARD_WRAP =
  "flex basis-full max-w-full px-2.5 ml-0 min-sm:basis-1/2 min-sm:max-w-[50%] min-lg:basis-[33.333%] min-lg:max-w-[33.333%]"

export const LayoutCards = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  cardType,
  buttons,
  cards,
  presetTheme,
  isHeaderBig,
  isWrapped,
  isButtonTopAligned,
  componentImagePriority,
  isLCP = false,
}: LayoutCards) => {
  const wrapperClasses = `${isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : ""}`
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `py-[100px] t2-lo-cards--t-${cardType} ${presetTheme || ""} ${colorSetting}`
  const headerClasses = isHeaderBig
    ? layoutHeaderBigClasses
    : layoutHeaderClasses
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const componentStyles = darkTextureWrapper(presetTheme || "")
  const hasEyebrow = eyebrow || subheader || description || header

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      data-testid="layout-cards"
    >
      <div className={wrapperClasses}>
        <div className="t2-layout__content">
          <div
            className={`flex flex-row flex-wrap mx-[-10px] flex-[0_1_auto] items-start ${hasEyebrow ? "pb-[50px]" : ""}`}
          >
            <div className={HEADER_AREA}>
              {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
              {finalHeader}
              {subheader && (
                <div className={layoutSubheaderClasses}>{subheader}</div>
              )}
              {description && (
                <CommonDescription
                  description={description}
                  className={"t2-lo-cards__description t2-layout__description"}
                />
              )}
            </div>
            {isButtonTopAligned && buttons && (
              <div
                className={`${TOP_CTA} t2-layout__top-cta hidden min-[800px]:flex`}
              >
                <SpecialButton
                  primary={true}
                  size="large"
                  label={buttons?.[0]?.label || ""}
                  destination={buttons?.[0]?.destination || ""}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap flex-[0_1_auto] flex-row mx-[-10px] [&_.t2-c]:mb-5">
            {cards &&
              cards.map((card, index) => (
                <div
                  key={`card-${index}`}
                  className={CARD_WRAP}
                  data-testid="card-general"
                >
                  <CardGeneral
                    {...card}
                    cardType={cardType || "bound"}
                    componentImagePriority={componentImagePriority}
                    isLCP={isLCP}
                  />
                </div>
              ))}
          </div>
        </div>
        {buttons && (
          <div
            className={`flex flex-wrap gap-y-7 gap-x-4 md:gap-y-4 max-sm:flex-col justify-center pt-[50px] ${isButtonTopAligned ? "min-[800px]:hidden" : ""}`}
          >
            {buttons.map((btn, index) => (
              <TerraLink key={`button-${btn.label}-${index}`} {...btn} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
